(function (root) {
  'use strict';
  const compatible = (entry, variantId) => entry.scope === 'all' ||
    (entry.scope === 'variants' && entry.keyboardVariants.includes(String(variantId)));
  const signature = (item) => JSON.stringify([
    String(item.variant_id || item.id),
    String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''),
    Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value != null)
      .sort(([a], [b]) => a.localeCompare(b)),
  ]);
  const quantityOf = (cart, item) => cart.items.reduce((sum, line) =>
    sum + (signature(line) === signature(item) ? line.quantity : 0), 0);
  const reconcile = (before, after, items) => items.map((item) => {
    const added = Math.max(0, quantityOf(after, item) - quantityOf(before, item));
    return { ...item, added, missing: Math.max(0, item.quantity - added) };
  });
  const selectVariant = (variants, selected, fallbackId, names) => {
    const options = Object.entries(selected || {});
    if (!options.length) return variants.find((variant) => String(variant.id) === String(fallbackId));
    return variants.find((variant) => options.length === variant.options.length && options.every(([key, value]) => {
      const index = names.indexOf(key);
      return index >= 0 && (variant[`option${index + 1}`] ?? variant.options[index]) === value;
    }));
  };
  class Purchase {
    constructor(fetcher, rootUrl) { this.fetcher = fetcher; this.rootUrl = rootUrl; this.busy = false; this.uncertain = false; }
    async request(path, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await this.fetcher(this.rootUrl + path, { ...options, signal: controller.signal, cache: 'no-store' });
        const data = await response.json();
        if (!response.ok || data.status >= 400 || data.errors) throw new Error(data.description || data.message || 'Cart request failed');
        return data;
      } finally { clearTimeout(timer); }
    }
    async add(items, sections, path) {
      if (this.busy || this.uncertain) return { blocked: true };
      this.busy = true;
      let before;
      let attempted = false;
      let data;
      let error;
      try {
        before = await this.request('cart.js');
        attempted = true;
        try {
          data = await this.request('cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections, sections_url: path }),
          });
        } catch (failure) { error = failure; }
        const after = await this.request('cart.js');
        const lines = reconcile(before, after, items);
        const complete = !error && lines.every((line) => line.missing === 0);
        this.uncertain = !complete;
        return { complete, lines, cart: after, sections: data?.sections, error };
      } catch (failure) {
        this.uncertain = attempted;
        return { complete: false, uncertain: attempted, error: failure };
      } finally { this.busy = false; }
    }
  }
  // A Storefront cart is separate from the Online Store's cookie-backed Ajax cart.
  // Never add/clear/restore that cart when starting an express checkout.
  const shopPayCartMutation = `mutation ProductShopPay($input: CartInput!, $lineCount: Int!) {
    cartCreate(input: $input) {
      cart {
        checkoutUrl
        lines(first: $lineCount) {
          nodes {
            quantity attributes { key value }
            merchandise { ... on ProductVariant { id } }
            sellingPlanAllocation { sellingPlan { id } }
          }
          pageInfo { hasNextPage }
        }
      }
      userErrors { message }
      warnings { message }
    }
  }`;
  class ShopPayCheckout {
    constructor(fetcher) { this.fetcher = fetcher; this.busy = false; }
    async create(items, { country, locale } = {}) {
      if (this.busy) return { blocked: true };
      this.busy = true;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        if (!items.length || items.length > 249 || items.some((item) => !/^\d+$/.test(String(item.id)) || !Number.isInteger(item.quantity) || item.quantity < 1)) {
          throw new Error('Invalid checkout selection');
        }
        const lines = items.map((item) => ({
          merchandiseId: `gid://shopify/ProductVariant/${item.id}`, quantity: item.quantity,
          attributes: Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value != null)
            .map(([key, value]) => ({ key, value: String(value) })),
          ...(item.selling_plan ? { sellingPlanId: `gid://shopify/SellingPlan/${item.selling_plan}` } : {}),
        }));
        const response = await this.fetcher('/api/2026-10/graphql.json', {
          method: 'POST', credentials: 'omit', cache: 'no-store', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query: shopPayCartMutation, variables: {
            input: { lines, buyerIdentity: { ...(country ? { countryCode: country } : {}), preferences: { wallet: ['shop_pay'] } } },
            lineCount: items.length + 1,
          } }),
        });
        const data = await response.json();
        const result = data.data?.cartCreate;
        if (!response.ok || data.errors?.length || result?.userErrors?.length || result?.warnings?.length || !result?.cart?.checkoutUrl) {
          throw new Error('Unable to create the complete checkout');
        }
        const cart = result.cart;
        const actual = cart.lines.nodes.map((line) => ({
          id: line.merchandise.id.split('/').pop(), quantity: line.quantity,
          properties: Object.fromEntries(line.attributes.map(({ key, value }) => [key, value])),
          selling_plan: line.sellingPlanAllocation?.sellingPlan.id.split('/').pop(),
        }));
        const totals = (entries) => {
          const map = new Map();
          entries.forEach((item) => map.set(signature(item), (map.get(signature(item)) || 0) + item.quantity));
          return map;
        };
        const expected = totals(items);
        const received = totals(actual);
        if (cart.lines.pageInfo.hasNextPage || expected.size !== received.size || [...expected].some(([key, quantity]) => received.get(key) !== quantity)) {
          throw new Error('Checkout selection changed');
        }
        const url = new URL(cart.checkoutUrl);
        if (url.protocol !== 'https:') throw new Error('Invalid checkout URL');
        url.searchParams.set('payment', 'shop_pay');
        if (locale) url.searchParams.set('locale', locale);
        return { complete: true, checkoutUrl: url.href };
      } catch (error) {
        // Retrying creates another isolated cart; it cannot increment the buyer's cart.
        return { complete: false, error };
      } finally {
        clearTimeout(timer);
        this.busy = false;
      }
    }
  }
  const api = { compatible, signature, reconcile, selectVariant, Purchase, ShopPayCheckout };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.KikAccessoryCore = api;
})(globalThis);
